import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApplicationSelection } from '../../shared/models/application.model';
import { IServerSettings } from '../../shared/models/serversettings';
import { ICustomerApplicationVariant, IIngestedDevicesChart, ISubmittedDevicesChart } from '../models';
import { IApplicationSettings } from '../models/app-settings.model';
import { ICustomer } from '../models/customer.model';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';

@Injectable()
export class CustomerService {
  private customerServiceUrl: string;
  private serverSettings: IServerSettings;
  private action: string;
  private apiGatewayUrl: string;
  private settings: IServerSettings;
  private applicationName = 'Not selected';
  settingsServiceUrl: string;

  applicationSettings: BehaviorSubject<IApplicationSettings> = new BehaviorSubject<IApplicationSettings>(null);

  private applicationSource = new BehaviorSubject<ApplicationSelection>(new ApplicationSelection(this.applicationName, 'Not selected'));
  applicationChanged$ = this.applicationSource.asObservable();

  private customerSource = new BehaviorSubject<ICustomer>(null);
  customerChanged$ = this.customerSource.asObservable();

  constructor (private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService) {
    this.serverSettings = this.settingsService.getSettings();
    this.customerServiceUrl = `${ this.serverSettings.CommonApiGatewayUrl }/ids/v1/customer/`;
    this.settingsServiceUrl = `${ this.serverSettings.CommonApiGatewayUrl }/set/v1/companies/`;
  }


  getCustomers(paramsContainer): Observable<ICustomer[]> {

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.customerServiceUrl }GetCustomers`, paramsContainer, { headers: headers }).pipe(
      map(this.extractData),
      catchError(this.handleErrors)
    );
  }

  getCustomerApplicationVariants(paramsContainer): Observable<ICustomerApplicationVariant[]> {

    const headers = this.authService.setHeader();

    const url = `${ this.customerServiceUrl }GetCustomerApplicationVariantsByApplicationUser`
      + `?applicationName=${ paramsContainer.applicationName }&userGuid=${ paramsContainer.userGuid }`;

    return this.http.post(url, {}, { headers: headers }).pipe(
      map(this.extractData),
      catchError(this.handleErrors)
    );
  }

  getCompanySetting(customerId: string, settingName: string): Observable<any> {

    const url = `${ this.settingsServiceUrl }SettingByName?settingName=${ settingName }`;

    let headers = this.authService.setHeader(false, true);
    headers = headers.set('customerId', customerId);

    return this.http.get(url, { headers: headers }).pipe(
      catchError(err => this.handleErrors(err))
    );
  }

  getCustomerIngestedDevicesChartData(paramsContainer): Observable<IIngestedDevicesChart[]> {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;

    this.action = `rpts/v1/Dashboard/GetClientIdDeviceInformation`;

    let headers = this.authService.setHeader(false, true);
    headers = headers.set('clientId', `${ paramsContainer.clientId }`);
    headers = headers.set('userGuid', paramsContainer.userGuid);

    return this.http.get<any>(`${ this.apiGatewayUrl }/${ this.action }`, { headers: headers }).pipe(
      catchError(err => this.handleErrors(err))
    );
  }

  getCustomerSubmittedDevicesChartData(paramsContainer): Observable<ISubmittedDevicesChart[]> {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;

    this.action = `sub/v1/Dashboard/GetClientIdSubmissionHistoryInformation`;

    let headers = this.authService.setHeader(false, true);
    headers = headers.set('clientId', `${ paramsContainer.clientId }`);
    headers = headers.set('userGuid', paramsContainer.userGuid);

    return this.http.get<any>(`${ this.apiGatewayUrl }/${ this.action }`, { headers: headers }).pipe(
      catchError(err => this.handleErrors(err))
    );
  }

  getApplications(): Observable<number> {
    const url = `${ this.customerServiceUrl }getapplications`;

    const headers = this.authService.setHeader();

    return this.http.post(url, null, { headers: headers }).pipe(
      map(this.extractData),
      catchError(this.handleErrors)
    );
  }

  getMigrationStatus(dataScope: string): Observable<any> {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;

    this.action = `rpts/v1/DataWarehouse/GetMigrationStatus`;

    let headers = this.authService.setHeader(false, false);

    return this.http.get<any>(`${this.apiGatewayUrl}/${this.action}`, { headers: headers }).pipe(
      catchError(err => this.handleErrors(err))
    );
  }

  getMigrationStatusWithRegistration(dataScope: string, registrationTypeSelection: string): Observable<any> {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;

    this.action = `rpts/v1/DataWarehouse/GetMigrationStatus`;

    let headers = this.authService.setHeader(false, false);

    let apiUrl = `${this.apiGatewayUrl}/${this.action}?registrationType=${registrationTypeSelection}`;

    return this.http.get<any>(apiUrl, { headers: headers }).pipe(
      catchError(err => this.handleErrors(err))
    );


    return this.http.get<any>(`${ this.apiGatewayUrl }/${ this.action }`, { headers: headers }).pipe(
      catchError(err => this.handleErrors(err))
    );
  }


  private extractData(response: Response) {

    if (response.status < 200 || response.status >= 300) {
      throw new Error('Bad response status: ' + response.status);
    }

    const body = response;

    return body || {};
  }

  public changeSelectedApplication(application: string): void {
    this.applicationName = application;
    this.applicationSource.next(new ApplicationSelection(application, '' /*this.serverSettings.Environment*/));
  }

  public changeSelectedCustomer(customer: ICustomer): void {

    this.customerSource.next(customer);
  }


  private handleErrors(error: any): Observable<any> {
    const errors: string[] = [];

    switch (error.status) {
      case 400: // Bad Request or Model State Error
        const err = error.json();
        if (err.modelState) {
          const valErrors = error.json().modelState;
          for (var key in valErrors) {
            for (var i = 0; i < valErrors[key].length; i++) {
              errors.push(valErrors[key][i]);
            }
          }
        } else if (err.message) {
          errors.push(err.message);
        } else {
          errors.push('An Unknown error occurred.');
        }
        break;

      case 404: // Not Found
        errors.push('Resource not found.');
        break;

      case 500: // Internal Error
        errors.push(error.json().exceptionMessage);
        break;

      default:
        errors.push('Status: ' + error.status
          + ' - Error Message: '
          + error.statusText);
        break;
    }
    // console.error('An error occurred', errors);

    return throwError(errors.join(';'));//Observable.throw(errors);
  }

  getIngestedDeviceChartDataMock(paramsContainer) {

    return this.http.get('../../../assets/mock/ingested-devices-chart.json');
  }

  getSubmittedDeviceChartDataMock(paramsContainer) {

    return this.http.get('../../../assets/mock/submitted-devices-chart.json');
  }

  getRevalidationCheckMock(datascope) {

    return this.http.get('../../../assets/mock/revalidation.json');
  }

}
